import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { useApi } from '@shopexperts/shared';
import type { AuthCheckResponse, AuthCheckClientResponse, AuthCheckExpertResponse } from '@shopexperts/shared';
import { clearStoredConvexTokenCache } from '@/app/composables/useConvexAuthToken';
import { useAuthStore } from '@/store/auth';

/**
 * Query key for auth check - exported for use in cache invalidation
 */
export const AUTH_CHECK_QUERY_KEY = ['auth', 'check'] as const;

/**
 * Client Auth Store - A Pinia store that wraps vue-query for auth state.
 * 
 * This store provides global access to the authenticated user's data
 * using vue-query for caching and automatic refetching.
 * 
 * @example
 * ```ts
 * const authStore = useClientAuthStore();
 * 
 * // Access reactive auth state
 * if (authStore.isAuthenticated && authStore.isClient) {
 *   console.log('Client:', authStore.client?.company_name);
 * }
 * 
 * // Manually refresh auth data
 * await authStore.refresh();
 * 
 * // Invalidate cache (e.g., after profile update)
 * authStore.invalidate();
 * ```
 */
export const useClientAuthStore = defineStore('clientAuth', () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const authStore = useAuthStore();

  // Token from legacy auth store. Keep this reactive so login-as/logout cannot
  // retain the auth-check result from a previous dashboard user.
  const token = ref(authStore.token || localStorage.getItem('CURRENT_TOKEN') || '');

  // Watch for token changes
  const hasToken = computed(() => !!token.value);

  // Vue Query for auth check
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: AUTH_CHECK_QUERY_KEY,
    queryFn: async (): Promise<AuthCheckResponse> => {
      return await api.public.auth.check();
    },
    enabled: hasToken,
    staleTime: 60000, // 1 minute
    retry: false,
  });

  watch(
    () => authStore.token,
    (nextToken, previousToken) => {
      if (nextToken === token.value) {
        return;
      }

      token.value = nextToken || '';
      if (!nextToken || nextToken !== previousToken) {
        queryClient.removeQueries({ queryKey: AUTH_CHECK_QUERY_KEY });
      }
    },
    { immediate: true, flush: 'sync' },
  );

  // ==================
  // Computed State
  // ==================

  /**
   * Whether the user is authenticated (has valid token and passed auth check)
   */
  const isAuthenticated = computed(() => {
    return hasToken.value && !!data.value && !isError.value;
  });

  /**
   * Whether the authenticated user is a client
   */
  const isClient = computed(() => {
    return isAuthenticated.value && data.value?.type === 'client';
  });

  /**
   * Whether the authenticated user is an expert
   */
  const isExpert = computed(() => {
    return isAuthenticated.value && data.value?.type === 'expert';
  });

  /**
   * The user type: 'client' or 'expert'
   */
  const userType = computed(() => data.value?.type);

  /**
   * Whether the user's email is validated
   */
  const isValidated = computed(() => data.value?.is_validated ?? false);

  /**
   * The authenticated user's basic info
   */
  const user = computed(() => data.value?.user);

  /**
   * Client-specific data (only available if user is a client)
   */
  const client = computed(() => {
    if (data.value?.type === 'client') {
      return (data.value as AuthCheckClientResponse).client;
    }
    return undefined;
  });

  /**
   * Organization data (only available if user is a client)
   */
  const organization = computed(() => {
    if (data.value?.type === 'client') {
      return (data.value as AuthCheckClientResponse).organization;
    }
    return undefined;
  });

  /**
   * Expert-specific data (only available if user is an expert)
   */
  const expert = computed(() => {
    if (data.value?.type === 'expert') {
      return (data.value as AuthCheckExpertResponse).expert;
    }
    return undefined;
  });

  /**
   * Full name computed from user data
   */
  const fullName = computed(() => {
    if (!user.value) return '';
    return `${user.value.first_name || ''} ${user.value.last_name || ''}`.trim();
  });

  /**
   * Display name (first name + last initial)
   */
  const displayName = computed(() => {
    if (!user.value) return 'User';
    const firstName = user.value.first_name || '';
    const lastName = user.value.last_name || '';
    if (firstName && lastName) {
      return `${firstName} ${lastName.charAt(0)}.`;
    }
    return firstName || lastName || 'User';
  });

  /**
   * Initials for avatar
   */
  const initials = computed(() => {
    if (!user.value) return '';
    const first = user.value.first_name?.charAt(0) || '';
    const last = user.value.last_name?.charAt(0) || '';
    return `${first}${last}`.toUpperCase();
  });

  /**
   * Profile picture URL (from client or expert data)
   */
  const profilePicture = computed(() => {
    if (isClient.value && client.value) {
      return client.value.profile_picture || null;
    }
    if (isExpert.value && expert.value) {
      return expert.value.profile_picture || null;
    }
    return null;
  });

  watch(isError, (nextIsError) => {
    if (nextIsError) {
      clearStoredConvexTokenCache();
    }
  });

  // ==================
  // Actions
  // ==================

  /**
   * Update the token (call this after login)
   */
  function setToken(newToken: string) {
    token.value = newToken;
  }

  /**
   * Clear the token (call this on logout)
   */
  function clearToken() {
    token.value = '';
    queryClient.removeQueries({ queryKey: AUTH_CHECK_QUERY_KEY });
  }

  /**
   * Refresh auth data from the server
   */
  async function refresh() {
    if (hasToken.value) {
      await refetch();
    }
  }

  /**
   * Invalidate the auth cache (triggers a refetch)
   */
  function invalidate() {
    queryClient.invalidateQueries({ queryKey: AUTH_CHECK_QUERY_KEY });
  }

  return {
    // Raw data
    data,
    
    // Auth status
    isAuthenticated,
    isClient,
    isExpert,
    isValidated,
    userType,
    
    // User data
    user,
    client,
    organization,
    expert,
    
    // Computed helpers
    fullName,
    displayName,
    initials,
    profilePicture,
    
    // Query state
    isLoading,
    isError,
    error,
    
    // Actions
    setToken,
    clearToken,
    refresh,
    invalidate,
  };
});
